import { reportError } from '@gef-ui/error-handler/actions';
import identity from "ramda/es/identity";
import isFunction from "ramda-extension/es/isFunction";
import isNotNil from "ramda-extension/es/isNotNil";
import { Subscriber } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, map, repeatWhen, takeUntil, tap } from 'rxjs/operators';
import { CALL_API } from '../../../constants';
import { callActionsWithArgs, createActionMeta, logApiError } from '../../utils';
import { conditionalRetryWhen } from '../operators';
import { createXhrError, parseXhrContent, resolveRequestParamsWithState } from '../utils';
const defaultXhrOkResponseResolver = (response)=>response.xhr.readyState === 4 ? response : Promise.reject(response);
/**
 * reactive api middleware
 *
 * @documented
 * @category api-middleware
 *
 * @param {String} [withCredentials=true] - default credentials configuration
 * @param {String} [frontendUrl] - frontend base URL
 * @param {Boolean} [customLogoutResolver] - custom logout resolver
 * @param {Function} [contentParser=parseXhrContent] - custom content parse
 * @param createXHR
 */ const reactiveApiMiddleware = ({ withCredentials, frontendUrl, customLogoutResolver, createXHR, contentParser = parseXhrContent } = {})=>({ getState, dispatch })=>(next)=>async (action)=>{
                const callAPI = action[CALL_API];
                if (!callAPI) {
                    return next(action);
                }
                const state = getState();
                const { onRequest, onSuccess, onError, onCancel } = callAPI.actions;
                const callNextActions = callActionsWithArgs(dispatch, callAPI.runInBatch);
                const requestParams = await resolveRequestParamsWithState(callAPI, withCredentials)(state);
                const actionMeta = createActionMeta(callAPI.meta);
                const resolveOkResponse = callAPI.customOkResponseResolver ?? defaultXhrOkResponseResolver;
                callNextActions(onRequest, [
                    null,
                    actionMeta
                ]);
                const subscriptions = [];
                const unsubscribeAfterResponse = ()=>{
                    subscriptions.forEach((subscription)=>subscription.unsubscribe());
                };
                if (callAPI.cancel) {
                    subscriptions.push(callAPI.cancel.subscribe({
                        next: ()=>callNextActions(onCancel, [
                                null,
                                actionMeta
                            ])
                    }));
                }
                const request$ = ajax({
                    ...createXHR ? {
                        createXHR
                    } : {},
                    ...isFunction(callAPI.progressCallback) ? {
                        progressSubscriber: new Subscriber(callAPI.progressCallback)
                    } : {},
                    ...requestParams
                });
                const conditionalOperators = [
                    callAPI.cancel ? takeUntil(callAPI.cancel) : undefined,
                    callAPI.retry ? repeatWhen(()=>callAPI.retry) : undefined
                ].filter(isNotNil);
                return request$.pipe(...conditionalOperators, tap(unsubscribeAfterResponse), map(resolveOkResponse), map((response)=>{
                    const { status, responseHeaders } = response;
                    const responseManipulation = callAPI.responseManipulation ?? identity;
                    return responseManipulation({
                        body: contentParser(response),
                        headers: responseHeaders,
                        status
                    });
                }), tap((parsedResponse)=>callNextActions(onSuccess, [
                        parsedResponse,
                        actionMeta
                    ])), conditionalRetryWhen(callAPI), catchError(async (response)=>{
                    let error = response;
                    const isXhrError = isNotNil(error.xhr);
                    // Response is not always API error, sometimes it can be even runtime error
                    if (isXhrError) {
                        error = await createXhrError(response, callAPI, requestParams, contentParser, actionMeta, action);
                    } else {
                        await logApiError(error.message);
                        const errorManipulation = callAPI.errorManipulation ?? identity;
                        error = errorManipulation(error, requestParams);
                    }
                    if (!callAPI.notReportError) {
                        dispatch(reportError(error));
                    }
                    callNextActions(onError, [
                        {
                            failure: error,
                            frontendUrl,
                            customLogoutResolver
                        },
                        actionMeta
                    ]);
                    throw error;
                })).toPromise();
            };
export default reactiveApiMiddleware;
